import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  X, Cpu, Plus, PencilSimple, TrashSimple, MagnifyingGlass,
  CloudArrowUp, DownloadSimple, Link, LinkBreak, CheckCircle,
  XCircle, WifiHigh, WifiSlash,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEVICE_TYPES = [
  "logger", "reefer", "cellular_tracker", "ble_logger",
  "gateway", "thermometer", "humidity_sensor", "shock_sensor", "other",
];
const STATUS_OPTIONS = ["registered", "online", "offline", "maintenance", "retired"];
const REF_TYPES = ["PO", "Order", "BOL", "AWB", "Container", "Load", "Shipment", "Custom"];

const EMPTY = {
  name: "", device_type: "logger", serial: "", imei: "", firmware_version: "",
  customer_device_id: "", asset_tag: "", status: "registered",
  assigned_shipment_id: "", assigned_shipment_reference: "",
  assigned_shipment_reference_type: "", active: true, notes: "",
};

/**
 * DeviceRegistry — pilot-ready CRUD & CSV import for cold-chain devices.
 * Independent from telemetry-derived `/api/fleet/devices`; owns the canonical
 * Device Registry surface at `/api/device-registry`.
 */
export default function DeviceRegistry({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);   // null | {... payload} | "new"
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [devs, ships] = await Promise.all([
        axios.get(`${API}/device-registry`),
        axios.get(`${API}/shipments/?limit=500`).catch(() => ({ data: [] })),
      ]);
      setItems(devs.data.items || []);
      const sList = Array.isArray(ships.data) ? ships.data : (ships.data?.items || []);
      setShipments(sList.slice(0, 500));
    } catch (e) {
      toast.error("Failed to load devices");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const filtered = items.filter((d) => {
    if (statusFilter !== "all") {
      if (statusFilter === "online" && !d.online) return false;
      if (statusFilter === "offline" && d.online) return false;
      if (statusFilter === "unassigned" && d.assigned_shipment_id) return false;
      if (statusFilter === "assigned" && !d.assigned_shipment_id) return false;
    }
    if (search) {
      const s = search.toLowerCase();
      return [d.name, d.serial, d.imei, d.asset_tag, d.customer_device_id, d.notes, d.device_type]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
    }
    return true;
  });

  const save = async (payload) => {
    try {
      // Strip empty strings → null so backend stores cleanly
      const body = Object.fromEntries(Object.entries(payload).map(
        ([k, v]) => [k, v === "" ? null : v]
      ));
      if (payload.id) {
        await axios.put(`${API}/device-registry/${payload.id}`, body);
        toast.success("Device updated");
      } else {
        await axios.post(`${API}/device-registry`, body);
        toast.success("Device created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete device "${d.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/device-registry/${d.id}`);
      toast.success("Device deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const assignShipment = async (d, shipment_id) => {
    try {
      await axios.post(`${API}/device-registry/${d.id}/assign-shipment`, {
        shipment_id: shipment_id || null,
      });
      toast.success(shipment_id ? "Assigned to shipment" : "Unassigned");
      load();
    } catch (e) {
      toast.error("Assign failed");
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/device-registry/import-csv/template`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "device_import_template.csv";
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/device-registry/import-csv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportReport(res.data);
      toast.success(`Import: ${res.data.inserted} new, ${res.data.updated} updated`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="fixed inset-0 z-[1900] bg-black/70 backdrop-blur-sm flex items-stretch justify-center"
      onClick={onClose}>
      <div className="bg-zinc-950 border-l border-zinc-800 w-full max-w-6xl ml-auto flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid="device-registry">

        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Cpu size={20} weight="duotone" className="text-teal-400" />
            <div>
              <div className="text-zinc-100 font-semibold flex items-center gap-2">
                Device Registry
                <span className="text-[10px] uppercase tracking-wider bg-teal-500/10 text-teal-400 border border-teal-500/30 px-2 py-0.5 rounded-sm">
                  Pilot Ready
                </span>
              </div>
              <div className="text-xs text-zinc-500">
                Canonical inventory of cold-chain hardware — independent from live telemetry feed.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadTemplate}
              className="border-zinc-700 hover:bg-zinc-800 text-xs" data-testid="device-csv-template-btn">
              <DownloadSimple size={13} className="mr-1.5" /> CSV Template
            </Button>
            <Button variant="outline" size="sm" disabled={importing}
              onClick={() => fileInputRef.current?.click()}
              className="border-zinc-700 hover:bg-zinc-800 text-xs" data-testid="device-csv-import-btn">
              <CloudArrowUp size={13} className="mr-1.5" />
              {importing ? "Importing…" : "Import CSV"}
            </Button>
            <input ref={fileInputRef} type="file" accept=".csv" className="hidden"
              onChange={handleCsvUpload} data-testid="device-csv-input" />
            <Button size="sm" onClick={() => setEditing({ ...EMPTY })}
              className="bg-teal-500 hover:bg-teal-400 text-zinc-950 text-xs"
              data-testid="device-add-btn">
              <Plus size={13} className="mr-1.5" /> Add Device
            </Button>
            <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200 ml-2"
              data-testid="device-registry-close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="px-6 py-3 border-b border-zinc-800 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-64">
            <MagnifyingGlass size={14} className="absolute left-3 top-2.5 text-zinc-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, serial, IMEI, asset tag…"
              className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-200 h-9 text-sm"
              data-testid="device-search" />
          </div>
          {["all", "online", "offline", "unassigned", "assigned"].map((f) => (
            <button key={f} onClick={() => setStatusFilter(f)}
              data-testid={`device-filter-${f}`}
              className={`text-xs px-3 py-1.5 rounded-sm border transition-colors capitalize ${
                statusFilter === f
                  ? "bg-teal-500/10 text-teal-300 border-teal-500/30"
                  : "border-zinc-800 text-zinc-400 hover:bg-zinc-900"
              }`}>
              {f}
            </button>
          ))}
          <div className="text-xs text-zinc-500 ml-auto font-mono">
            {filtered.length} / {items.length}
          </div>
        </div>

        {/* Import report banner */}
        {importReport && (
          <div className="px-6 py-2 bg-emerald-500/5 border-b border-emerald-500/20 text-xs text-emerald-300 flex items-center gap-3">
            <CheckCircle size={14} weight="duotone" />
            <span>Imported: <b>{importReport.inserted}</b> new · <b>{importReport.updated}</b> updated</span>
            {importReport.errors?.length > 0 && (
              <span className="text-amber-300">· {importReport.errors.length} error rows</span>
            )}
            <button onClick={() => setImportReport(null)}
              className="ml-auto text-zinc-500 hover:text-zinc-200" data-testid="dismiss-import-report">
              <X size={12} />
            </button>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="p-8 text-center text-xs text-zinc-500 font-mono">Loading…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="p-12 text-center text-zinc-500">
              <Cpu size={40} weight="thin" className="mx-auto mb-3 text-zinc-700" />
              <div className="text-sm">No devices in the registry yet.</div>
              <div className="text-xs mt-2">Add one manually or import via CSV.</div>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <table className="w-full text-xs" data-testid="device-table">
              <thead className="sticky top-0 bg-zinc-950 border-b border-zinc-800">
                <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="text-left px-4 py-2 font-medium">Device</th>
                  <th className="text-left px-3 py-2 font-medium">Type</th>
                  <th className="text-left px-3 py-2 font-medium">Serial / IMEI</th>
                  <th className="text-left px-3 py-2 font-medium">Asset / Customer ID</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium">Shipment</th>
                  <th className="text-left px-3 py-2 font-medium">Last Seen</th>
                  <th className="text-right px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.id} className="border-b border-zinc-900 hover:bg-zinc-900/40"
                    data-testid={`device-row-${d.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-zinc-200 flex items-center gap-2">
                        {d.online
                          ? <WifiHigh size={12} weight="bold" className="text-emerald-400" />
                          : <WifiSlash size={12} weight="bold" className="text-zinc-600" />}
                        {d.name}
                      </div>
                      <div className="text-[10px] text-zinc-500 font-mono">{d.firmware_version || "—"}</div>
                    </td>
                    <td className="px-3 py-2.5 text-zinc-400 font-mono">{d.device_type}</td>
                    <td className="px-3 py-2.5 font-mono text-zinc-400">
                      <div>{d.serial || "—"}</div>
                      <div className="text-[10px] text-zinc-600">{d.imei || ""}</div>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-zinc-400">
                      <div>{d.asset_tag || "—"}</div>
                      <div className="text-[10px] text-zinc-600">{d.customer_device_id || ""}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] uppercase font-medium px-2 py-0.5 rounded-sm border ${
                        d.status === "online" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" :
                        d.status === "offline" ? "bg-red-500/10 text-red-300 border-red-500/30" :
                        d.status === "maintenance" ? "bg-amber-500/10 text-amber-300 border-amber-500/30" :
                        d.status === "retired" ? "bg-zinc-700/30 text-zinc-500 border-zinc-700" :
                        "bg-sky-500/10 text-sky-300 border-sky-500/30"}`}>
                        {d.status || "registered"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <select value={d.assigned_shipment_id || ""}
                        onChange={(e) => assignShipment(d, e.target.value)}
                        className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs rounded-sm px-2 py-1 max-w-40 truncate"
                        data-testid={`device-assign-${d.id}`}>
                        <option value="">— Unassigned —</option>
                        {shipments.map((s) => (
                          <option key={s.id} value={s.id}>
                            {(s.name || s.id || "").slice(0, 40)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2.5 text-zinc-500 font-mono text-[10px]">
                      {d.last_seen ? new Date(d.last_seen).toLocaleString() : "never"}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => setEditing({ ...d })}
                        className="text-zinc-400 hover:text-teal-300 p-1"
                        data-testid={`device-edit-${d.id}`}>
                        <PencilSimple size={14} />
                      </button>
                      <button onClick={() => remove(d)}
                        className="text-zinc-500 hover:text-red-300 p-1 ml-1"
                        data-testid={`device-delete-${d.id}`}>
                        <TrashSimple size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Editor modal (in-flow overlay) */}
        {editing && (
          <DeviceEditor
            initial={editing}
            shipments={shipments}
            onCancel={() => setEditing(null)}
            onSave={save}
          />
        )}
      </div>
    </div>
  );
}

function DeviceEditor({ initial, shipments, onCancel, onSave }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isNew = !initial.id;

  return (
    <div className="fixed inset-0 z-[1950] bg-black/80 flex items-center justify-center p-6"
      onClick={onCancel}>
      <Card className="bg-zinc-950 border-zinc-800 w-full max-w-2xl p-6"
        onClick={(e) => e.stopPropagation()} data-testid="device-editor">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-zinc-100 font-semibold text-base">
            {isNew ? "Add Device" : `Edit · ${initial.name}`}
          </h3>
          <button onClick={onCancel} className="text-zinc-500 hover:text-zinc-200" data-testid="device-editor-close">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Device Name *" required>
            <Input value={form.name} onChange={(e) => update("name", e.target.value)}
              placeholder="Reefer Unit 17"
              className="bg-zinc-900 border-zinc-800 text-zinc-200"
              data-testid="device-form-name" />
          </Field>
          <Field label="Device Type">
            <Select value={form.device_type} onChange={(v) => update("device_type", v)}
              options={DEVICE_TYPES} testid="device-form-type" />
          </Field>
          <Field label="Serial Number">
            <Input value={form.serial || ""} onChange={(e) => update("serial", e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-200"
              data-testid="device-form-serial" />
          </Field>
          <Field label="IMEI">
            <Input value={form.imei || ""} onChange={(e) => update("imei", e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-200 font-mono"
              data-testid="device-form-imei" />
          </Field>
          <Field label="Firmware Version">
            <Input value={form.firmware_version || ""} onChange={(e) => update("firmware_version", e.target.value)}
              placeholder="1.4.2"
              className="bg-zinc-900 border-zinc-800 text-zinc-200 font-mono"
              data-testid="device-form-firmware" />
          </Field>
          <Field label="Status">
            <Select value={form.status} onChange={(v) => update("status", v)}
              options={STATUS_OPTIONS} testid="device-form-status" />
          </Field>
          <Field label="Customer Device ID">
            <Input value={form.customer_device_id || ""} onChange={(e) => update("customer_device_id", e.target.value)}
              placeholder="RTE-CDI-001"
              className="bg-zinc-900 border-zinc-800 text-zinc-200 font-mono"
              data-testid="device-form-customer-id" />
          </Field>
          <Field label="Asset Tag">
            <Input value={form.asset_tag || ""} onChange={(e) => update("asset_tag", e.target.value)}
              placeholder="AT-12345"
              className="bg-zinc-900 border-zinc-800 text-zinc-200 font-mono"
              data-testid="device-form-asset-tag" />
          </Field>

          <Field label="Assigned Shipment">
            <select value={form.assigned_shipment_id || ""} onChange={(e) => update("assigned_shipment_id", e.target.value)}
              className="bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm rounded-md px-3 py-2 w-full"
              data-testid="device-form-shipment">
              <option value="">— None —</option>
              {shipments.map((s) => (
                <option key={s.id} value={s.id}>{(s.name || s.id || "").slice(0, 60)}</option>
              ))}
            </select>
          </Field>
          <Field label="Shipment Reference (Optional)">
            <div className="flex gap-2">
              <select value={form.assigned_shipment_reference_type || ""}
                onChange={(e) => update("assigned_shipment_reference_type", e.target.value)}
                className="bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm rounded-md px-2 py-2 w-28"
                data-testid="device-form-ref-type">
                <option value="">Type</option>
                {REF_TYPES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <Input value={form.assigned_shipment_reference || ""}
                onChange={(e) => update("assigned_shipment_reference", e.target.value)}
                placeholder="PO-87423"
                className="bg-zinc-900 border-zinc-800 text-zinc-200 flex-1 font-mono"
                data-testid="device-form-ref-value" />
            </div>
          </Field>

          <div className="col-span-2">
            <Field label="Notes">
              <Textarea value={form.notes || ""} onChange={(e) => update("notes", e.target.value)}
                rows={2} placeholder="Internal notes / asset history…"
                className="bg-zinc-900 border-zinc-800 text-zinc-200"
                data-testid="device-form-notes" />
            </Field>
          </div>

          <div className="col-span-2 flex items-center gap-2">
            <input id="active" type="checkbox" checked={form.active}
              onChange={(e) => update("active", e.target.checked)}
              className="accent-teal-500" data-testid="device-form-active" />
            <label htmlFor="active" className="text-xs text-zinc-300">Device is active (eligible for offline alerts)</label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-zinc-900">
          <Button variant="outline" onClick={onCancel}
            className="border-zinc-700 hover:bg-zinc-800" data-testid="device-form-cancel">
            Cancel
          </Button>
          <Button onClick={() => {
            if (!form.name?.trim()) { toast.error("Device Name is required"); return; }
            onSave(form);
          }} className="bg-teal-500 hover:bg-teal-400 text-zinc-950" data-testid="device-form-save">
            <CheckCircle size={14} weight="bold" className="mr-1.5" />
            {isNew ? "Create" : "Save Changes"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">
        {label}{required && <span className="text-teal-400">*</span>}
      </Label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm rounded-md px-3 py-2 w-full capitalize"
      data-testid={testid}>
      {options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
    </select>
  );
}
